// A crash inside a page used to unmount the whole app and leave a white
// screen — nothing on it to read, nothing to report, and no way to tell a
// crash apart from a slow request. This catches it, keeps the sidebar and the
// rest of the portal alive, and shows the error text so it can be quoted.
//
// `resetKey` changes when the page changes: switching away from a broken page
// clears the error instead of leaving the message on screen for ever.
import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Kept in the console too, so the full stack is available even after the
    // details below are collapsed.
    console.error('Page crashed:', error, info?.componentStack);
  }

  componentDidUpdate(prev) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, info: null });
    }
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="card" style={{ padding: 20, borderColor: 'var(--red)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: 'var(--red)' }}>
          This page could not be displayed
        </h3>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Something in the page failed while drawing it. The rest of the portal is
          unaffected — use the menu to carry on. Please send the message below to IT.
        </p>
        <pre style={{
          fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
          padding: 10, borderRadius: 6, background: 'var(--bg2)',
          border: '1px solid var(--border)', color: 'var(--text)',
        }}>{String(error?.message || error)}</pre>
        {info?.componentStack && (
          <details style={{ marginTop: 10 }}>
            <summary style={{ fontSize: 12.5, cursor: 'pointer', color: 'var(--text2)' }}>
              Technical details
            </summary>
            <pre style={{
              fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              marginTop: 8, padding: 10, borderRadius: 6, background: 'var(--bg2)',
              border: '1px solid var(--border)', color: 'var(--text2)', maxHeight: 260,
              overflow: 'auto',
            }}>{info.componentStack}</pre>
          </details>
        )}
        <div className="flex-gap" style={{ gap: 8, marginTop: 12 }}>
          <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => this.setState({ error: null, info: null })}>
            Try again
          </button>
          <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => window.location.reload()}>
            Reload the portal
          </button>
        </div>
      </div>
    );
  }
}
